export const MAX_COMPARE_PRODUCTS = 3;

export const COMPARABLE_PRODUCT_TYPES = [
  "drug",
  "biologic",
  "otc",
  "vaccine",
];

const comparableTypes = new Set(COMPARABLE_PRODUCT_TYPES);

const firstText = (...values) => {
  for (const value of values) {
    const candidates = Array.isArray(value) ? value : [value];
    const match = candidates.find(
      (candidate) =>
        typeof candidate === "string" && candidate.trim().length > 0
    );

    if (match) return match.trim();
  }

  return "";
};

const completeGenericNames = (product) => {
  for (const value of [
    product?.genericNames,
    product?.openfda?.generic_name,
    product?.identity?.genericNames,
    product?.genericName,
    product?.generic_name,
  ]) {
    const names = (Array.isArray(value) ? value : [value])
      .filter((name) => typeof name === "string" && name.trim())
      .map((name) => name.trim());
    if (names.length) return names;
  }
  // An older tray's generic display may contain only the first of several
  // generic names. Keep its alias identity rather than guessing a full set.
  return [];
};

export const isComparableProductType = (type) =>
  comparableTypes.has(String(type || "").trim().toLowerCase());

export const getCompareProductKey = (product) => {
  const type = String(product?.type || "").trim().toLowerCase();
  const productAlias = String(
    product?.productAlias || product?.slug || ""
  ).trim();

  return type && productAlias ? `${type}:${productAlias}` : "";
};

const compareFamilyKey = (product) => {
  const type = String(product?.type || "").trim().toLowerCase();
  const family = firstText(product?.catalogDisplayFamily, product?.identity?.catalogDisplayFamily);
  if (!family || !isComparableProductType(type)) return "";
  const category = type === "drug" || type === "biologic" ? "medication" : type;
  return JSON.stringify([category, family]);
};
const compareFamilyIds = (product) => {
  const ids = product?.productFamilyIds || product?.identity?.productFamilyIds;
  return Array.isArray(ids) ? [...new Set(ids
    .filter((id) => typeof id === "string" && id.trim()).map((id) => id.trim()))] : [];
};

/** Family metadata only affects selection; URLs keep the real type and alias. */
export const isSameCompareProduct = (first, second) => {
  const firstKey = getCompareProductKey(first);
  if (firstKey && firstKey === getCompareProductKey(second)) return true;
  const firstFamily = compareFamilyKey(first);
  if (firstFamily && firstFamily === compareFamilyKey(second)) return true;
  const category = (product) => {
    const type = String(product?.type || "").trim().toLowerCase();
    return type === "drug" || type === "biologic" ? "medication" : type;
  };
  if (!isComparableProductType(first?.type) || !isComparableProductType(second?.type)
    || category(first) !== category(second)) return false;
  // Old session summaries have exact source IDs but stale or missing family
  // keys. A server-resolved member list proves membership without name guesses.
  const firstId = firstText(first?.id, first?._id, first?.productId);
  const secondId = firstText(second?.id, second?._id, second?.productId);
  return Boolean((firstId && compareFamilyIds(second).includes(firstId))
    || (secondId && compareFamilyIds(first).includes(secondId)));
};

/**
 * Product cards use several response shapes. Keep only the small, serializable
 * identity/display summary required by the global tray. The comparison API is
 * responsible for returning the full clinical data.
 */
export const normalizeCompareProduct = (product) => {
  const type = String(product?.type || product?.productType || "")
    .trim()
    .toLowerCase();
  const productAlias = String(
    product?.productAlias || product?.slug || ""
  ).trim();

  if (!isComparableProductType(type) || !productAlias) return null;

  const brand = firstText(
    product?.brand,
    product?.brandName,
    product?.name,
    product?.productName,
    product?.openfda?.brand_name,
    product?.openfda?.generic_name,
    productAlias
  );

  return {
    id: String(product?.id || product?._id || product?.productId || "").trim(),
    type,
    productAlias,
    brand,
    generic: firstText(
      product?.generic,
      product?.genericName,
      product?.generic_name,
      product?.openfda?.generic_name
    ),
    genericNames: completeGenericNames(product),
    ...(firstText(product?.catalogDisplayFamily, product?.identity?.catalogDisplayFamily) && {
      catalogDisplayFamily: firstText(product?.catalogDisplayFamily, product?.identity?.catalogDisplayFamily),
    }),
    ...(compareFamilyIds(product).length && { productFamilyIds: compareFamilyIds(product) }),
    manufacturer: firstText(
      product?.manufacturer,
      product?.manufacturerName,
      product?.manufacturer_name,
      product?.openfda?.manufacturer_name
    ),
  };
};

export const normalizeCompareProducts = (products) => {
  const unique = [];

  (Array.isArray(products) ? products : []).forEach((product) => {
    const normalized = normalizeCompareProduct(product);
    if (normalized && !unique.some((selected) => isSameCompareProduct(selected, normalized))) {
      unique.push(normalized);
    }
  });

  return unique.slice(0, MAX_COMPARE_PRODUCTS);
};

export const buildCompareProductsParam = (products) =>
  normalizeCompareProducts(products)
    .map((product) => getCompareProductKey(product))
    .join(",");

// Reps browse the same Quick Compare screen, but under their own /reps URL —
// secureRoutes redirects a rep away from any /provider/* path.
export const REPS_COMPARE_PATH = "/reps/compare";
export const PROVIDER_COMPARE_PATH = "/provider/compare";

export const getComparePath = (userType) =>
  userType === "reps" ? REPS_COMPARE_PATH : PROVIDER_COMPARE_PATH;

export const isComparePath = (pathname) =>
  Boolean(
    pathname?.startsWith(PROVIDER_COMPARE_PATH) ||
      pathname?.startsWith(REPS_COMPARE_PATH)
  );
